/**
 * 钱包域 API:收益(earnings)+ 收款账户(withdraw-accounts)。
 * 类型引用生成的 `components['schemas']`。后端契约只读消费(零后端改动)。
 *
 * FR-12=B:**无主动提现端点**。本域提供:
 *  - 收益只读聚合:summary + history(真实结算 5%/95、T+N)。
 *  - 收款账户管理:列出 / 绑银行卡 / 切主卡 / 微信绑定(现有端点)。
 *  - 「到账记录」= earnings/history 的已打款流水(T+N 自动结算,不另立提现流水)。
 */
import { call } from './client';
import type { components } from './openapi.generated';

type Schemas = components['schemas'];
export type EarningsSummaryResponse = Schemas['EarningsSummaryResponse'];
export type EarningsHistoryResponse = Schemas['EarningsHistoryResponse'];
export type PendingSettlementItem = Schemas['PendingSettlementItem'];
export type PaidSettlementItem = Schemas['PaidSettlementItem'];
export type WithdrawAccountView = Schemas['WithdrawAccountView'];
export type BindBankCardRequest = Schemas['BindBankCardRequest'];
export type WechatAuthStartResponse = Schemas['WechatAuthStartResponse'];

/** 收益汇总 + 待打款明细(FR-11)。userId 取自 JWT,不传参。 */
export function getEarningsSummary(): Promise<EarningsSummaryResponse> {
  return call<EarningsSummaryResponse>({ method: 'GET', url: '/api/v1/wallet/earnings/summary' });
}

/** 已打款历史分页(page 从 1 起 ≤200;size 默认 20 ≤50)。 */
export function getEarningsHistory(page = 1, size = 20): Promise<EarningsHistoryResponse> {
  return call<EarningsHistoryResponse>({
    method: 'GET',
    url: '/api/v1/wallet/earnings/history',
    params: { page, size },
  });
}

/** 列出当前用户收款账户(主卡优先、脱敏)。 */
export function listWithdrawAccounts(): Promise<WithdrawAccountView[]> {
  return call<WithdrawAccountView[]>({
    method: 'GET',
    url: '/api/v1/wallet/withdraw-accounts/current',
  });
}

/** 绑定银行卡(四要素)。成功推态 KYC_VERIFIED → WITHDRAW_BOUND。 */
export function bindBankCard(req: BindBankCardRequest): Promise<Schemas['WithdrawAccountBindResponse']> {
  return call<Schemas['WithdrawAccountBindResponse']>({
    method: 'POST',
    url: '/api/v1/wallet/withdraw-accounts',
    data: req,
  });
}

/** 切换主卡(需二次确认 confirmed=true)。 */
export function switchPrimaryAccount(id: string): Promise<Schemas['WithdrawAccountBindResponse']> {
  return call<Schemas['WithdrawAccountBindResponse']>({
    method: 'POST',
    url: `/api/v1/wallet/withdraw-accounts/${encodeURIComponent(id)}/switch-primary`,
    data: { confirmed: true } satisfies Schemas['SwitchPrimaryRequest'],
  });
}

/** 启动微信钱包绑定授权 → 返回授权 URL + 一次性 state。 */
export function startWechatBind(): Promise<WechatAuthStartResponse> {
  return call<WechatAuthStartResponse>({
    method: 'POST',
    url: '/api/v1/wallet/withdraw-accounts/wechat/start',
  });
}

/** 微信钱包绑定回调(用 state+code 换 openid 落库)。 */
export function callbackWechatBind(
  state: string,
  code: string,
): Promise<Schemas['WithdrawAccountBindResponse']> {
  return call<Schemas['WithdrawAccountBindResponse']>({
    method: 'POST',
    url: '/api/v1/wallet/withdraw-accounts/wechat/callback',
    data: { state, code } satisfies Schemas['BindWechatCallbackRequest'],
  });
}
